/**
 * DeviceContactsCard — decrypt + view a device's synced contact book.
 *
 * THIRD-PARTY PII (the names / phones / emails of the customer's address
 * book). The card is rendered only for users with `device.contacts.view`
 * (platform_admin + recovery_officer) — the parent gates on that
 * permission. Contacts are NOT fetched on page load; only on an explicit
 * "Reveal" click, and every reveal goes through the server-side audit log
 * (action='device.contacts.view', COUNT-only payload — the PII is never
 * logged). Closing the card drops the decrypted data from memory.
 *
 * The device syncs its address book WEEKLY (sealed AES-256-GCM at rest); an
 * empty result means it has never synced — a fresh enrolment whose weekly
 * tick hasn't fired, READ_CONTACTS denied, or a non-android-do device.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Badge } from "./Badge.js";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { api } from "../lib/api.js";

interface ContactEntry {
  readonly displayName: string;
  readonly phoneNumbers: readonly string[];
  readonly emails?: readonly string[];
}

interface DeviceContactsResponse {
  readonly deviceId: string;
  readonly loanId: string | null;
  readonly contactCount: number;
  readonly syncedAt: string | null;
  readonly contacts: readonly ContactEntry[];
}

export function DeviceContactsCard({ deviceId }: { readonly deviceId: string }): JSX.Element {
  const [data, setData] = useState<DeviceContactsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reveal = useMutation({
    mutationFn: () => api.get<DeviceContactsResponse>(`/api/devices/${deviceId}/contacts`),
    onSuccess: (res) => {
      setData(res);
      setError(null);
    },
    onError: (err: unknown) => {
      setData(null);
      const e = err as { status?: number; problem?: { detail?: string } };
      setError(e.problem?.detail ?? "Failed to load contacts.");
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Synced contacts</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-slate-600">
          The customer&apos;s device address book, synced weekly and sealed (encrypted) at rest.
          This is third-party personal data — every reveal is audited and reserved for recovery.
        </p>

        {data === null ? (
          <div className="flex items-center gap-3">
            <Button onClick={() => reveal.mutate()} disabled={reveal.isPending} variant="primary">
              {reveal.isPending ? "Decrypting…" : "Reveal contacts"}
            </Button>
            {error !== null ? <span className="text-xs text-red-700">{error}</span> : null}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone="brand">{data.contactCount} contacts</Badge>
              {data.syncedAt !== null ? (
                <span className="text-xs text-slate-500">
                  Last synced {new Date(data.syncedAt).toLocaleString()}
                </span>
              ) : (
                <span className="text-xs text-slate-500">Never synced from this device.</span>
              )}
              <Button onClick={() => setData(null)} variant="ghost">
                Hide
              </Button>
            </div>

            {data.contacts.length === 0 ? (
              <p className="text-xs text-slate-500">
                No contacts on file — the device hasn&apos;t completed a weekly contact sync yet
                (fresh enrolment, contacts permission denied, or a non-DPC device).
              </p>
            ) : (
              <ul className="max-h-96 divide-y divide-simuflex-line overflow-y-auto rounded-md border border-simuflex-line">
                {data.contacts.map((c, idx) => (
                  <li key={`${idx}-${c.phoneNumbers[0] ?? c.displayName}`} className="px-3 py-2">
                    <div className="font-medium text-slate-800">
                      {c.displayName.trim().length > 0 ? c.displayName : "(no name)"}
                    </div>
                    {c.phoneNumbers.length > 0 ? (
                      <div className="font-mono text-xs text-slate-600">
                        {c.phoneNumbers.join(", ")}
                      </div>
                    ) : null}
                    {c.emails && c.emails.length > 0 ? (
                      <div className="text-xs text-slate-500">{c.emails.join(", ")}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
